import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { api, money, sized, srcSet, sanitizeStoryHtml } from "../lib/api";

export default function StoryDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [artwork, setArtwork] = useState([]);
  const [more, setMore] = useState([]);
  const [routeLink, setRouteLink] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setS(null); setNotFound(false); setRouteLink(null);
    api.get(`/stories/${slug}`).then((r) => {
      setS(r.data);
      if (r.data.related_artwork_slugs?.length) {
        Promise.all(r.data.related_artwork_slugs.map((x) => api.get(`/products/${x}`).then((y) => y.data).catch(() => null)))
          .then((arr) => setArtwork(arr.filter(Boolean)));
      } else setArtwork([]);
    }).catch(() => {
      // Story missing: honour a 301 redirect (e.g. a corrected slug) before showing not-found,
      // so an old/changed URL lands on the canonical story instead of a dead end.
      api.get("/redirects").then((r) => {
        const here = `/stories/${slug}`;
        const m = (r.data || []).find((x) => x.old_path === here);
        if (m) navigate(m.new_path, { replace: true });
        else setNotFound(true);
      }).catch(() => setNotFound(true));
    });
    api.get("/stories").then((r) => setMore(r.data.filter((x) => x.slug !== slug).slice(0, 3)));
    api.get(`/photos/story-route/${slug}`).then((r) => setRouteLink(r.data.route)).catch(() => setRouteLink(null));
  }, [slug, navigate]);

  if (notFound) return <div className="max-w-3xl mx-auto px-5 py-32 text-center"><h1 className="font-display text-4xl">Story not found</h1><Link to="/stories" className="link-underline mt-4 inline-block">Back to stories</Link></div>;
  if (!s) return <div className="max-w-3xl mx-auto px-5 py-32"><div className="animate-pulse h-96 bg-[#F0EFEA]" /></div>;

  const absHero = s.hero_image && s.hero_image.startsWith("/") ? window.location.origin + s.hero_image : s.hero_image;
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Article", headline: s.title, image: [absHero], datePublished: s.published_at,
      author: { "@type": "Person", name: s.author }, publisher: { "@type": "Organization", name: "CaminoSapiens" }, description: s.excerpt },
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Stories", item: `${window.location.origin}/stories` },
      { "@type": "ListItem", position: 2, name: s.title, item: window.location.href } ] },
  ];

  return (
    <article className="py-14" data-testid="story-detail">
      <Seo title={s.seo?.title || `${s.title} — CaminoSapiens`} description={s.seo?.description || s.excerpt} image={s.seo?.og_image || s.hero_image} noindex={s.seo?.noindex} jsonLd={jsonLd} />
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Stories", to: "/stories" }, { label: s.title }]} />
        <p className="overline">{s.published_at} · {s.author}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mt-3" data-testid="story-title">{s.title}</h1>
      </div>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 mt-10">
        <img src={sized(s.hero_image, 1600)} srcSet={srcSet(s.hero_image, [600, 1000, 1600])} sizes="(min-width:640px) 900px, 100vw" alt={s.hero_alt || s.title} className="w-full h-[440px] sm:h-[560px] object-cover border border-[#E4E1DB]" />
      </div>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-12 prose-camino">
        {s.body_is_html
          ? <div className="story-html" dangerouslySetInnerHTML={{ __html: sanitizeStoryHtml(s.body) }} data-testid="story-body" />
          : s.body.split("\n\n").map((para, i) => <p key={i}>{para}</p>)}
      </div>

      {routeLink && (
        <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-14 border-t border-[#E4E1DB] pt-8 flex flex-wrap items-baseline justify-between gap-3" data-testid="story-route-link">
          <p className="text-[#4A4947]">See the photographs from this walk.</p>
          <Link to={`/photography/routes/${routeLink.slug}`} data-testid="story-route-cta" className="inline-flex items-center gap-2 text-sm link-underline text-[#1C1B1A]">
            Photographs from the {routeLink.label} →
          </Link>
        </div>
      )}

      {s.gallery?.length > 0 && (
        <div className="max-w-5xl mx-auto px-5 sm:px-8 mt-12 grid grid-cols-2 gap-4">
          {s.gallery.map((g, i) => <img key={i} src={sized(g, 900)} srcSet={srcSet(g)} sizes="(min-width:640px) 50vw, 100vw" alt="" className="w-full h-64 object-cover border border-[#E4E1DB]" loading="lazy" />)}
        </div>
      )}

      {artwork.length > 0 && (
        <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-16 border-t border-[#E4E1DB] pt-10">
          <p className="overline mb-6">Related Artwork</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {artwork.map((a) => (
              <Link key={a.slug} to={`/etchings/${a.slug}`} className="group flex gap-4 items-center" data-testid={`story-artwork-${a.slug}`}>
                <img src={sized(a.primary_image, 200)} alt={a.image_alt || a.title} className="w-24 h-24 object-cover border border-[#E4E1DB]" />
                <div>
                  <h3 className="font-display text-xl">{a.title}</h3>
                  <p className="text-sm text-[#4A4947]">{money(a.price, a.currency)}</p>
                  <span className="link-underline text-xs">View etching</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {more.length > 0 && (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-24">
          <p className="overline mb-8">More Stories</p>
          <div className="grid md:grid-cols-3 gap-8">
            {more.map((m) => (
              <Link key={m.slug} to={`/stories/${m.slug}`} className="group block">
                <div className="overflow-hidden border border-[#E4E1DB]">
                  <img src={sized(m.hero_image, 700)} srcSet={srcSet(m.hero_image, [400, 700])} sizes="(min-width:768px) 33vw, 100vw" alt={m.hero_alt || m.title} className="w-full h-52 object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
                </div>
                <h3 className="font-display text-lg mt-3">{m.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
